import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import FactoryRoundedIcon from '@mui/icons-material/FactoryRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';

const VIEWS = [
    { id: 'floor-deck', label: 'Floor Deck', Icon: DashboardRoundedIcon },
    { id: 'pep', label: 'PE&P', Icon: FactoryRoundedIcon },
    { id: 'inventory', label: 'Inventory', Icon: Inventory2RoundedIcon },
];

function HomePageTabs({ activeView, onViewChange }) {
    const navRef = useRef(null);
    const tabRefs = useRef({});
    const pendingTimer = useRef(null);
    const indicatorReadyFrame = useRef(null);
    const [indicator, setIndicator] = useState({ left: 0, width: 0 });
    const [pendingView, setPendingView] = useState(null);
    const [isIndicatorReady, setIsIndicatorReady] = useState(false);
    const displayedActiveView = pendingView || activeView;

    const clearPendingView = () => {
        if (pendingTimer.current) {
            window.clearTimeout(pendingTimer.current);
            pendingTimer.current = null;
        }
    };

    const handleViewChange = (viewId) => {
        clearPendingView();

        if (viewId === activeView) {
            setPendingView(null);
            return;
        }

        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setPendingView(null);
            onViewChange(viewId);
            return;
        }

        setPendingView(viewId);
        pendingTimer.current = window.setTimeout(() => {
            pendingTimer.current = null;
            onViewChange(viewId);
        }, 220);
    };

    useEffect(() => {
        indicatorReadyFrame.current = window.requestAnimationFrame(() => {
            indicatorReadyFrame.current = null;
            setIsIndicatorReady(true);
        });

        return () => {
            clearPendingView();
            if (indicatorReadyFrame.current) window.cancelAnimationFrame(indicatorReadyFrame.current);
        };
    }, []);

    useLayoutEffect(() => {
        const updateIndicator = () => {
            const nav = navRef.current;
            const activeTab = tabRefs.current[displayedActiveView];
            if (!nav || !activeTab) return;

            const navRect = nav.getBoundingClientRect();
            const tabRect = activeTab.getBoundingClientRect();
            setIndicator({
                left: tabRect.left - navRect.left + nav.scrollLeft,
                width: tabRect.width,
            });
        };

        updateIndicator();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateIndicator);
        if (observer && navRef.current) observer.observe(navRef.current);
        window.addEventListener('resize', updateIndicator);

        return () => {
            if (observer) observer.disconnect();
            window.removeEventListener('resize', updateIndicator);
        };
    }, [displayedActiveView]);

    return (
        <nav ref={navRef} className="floor-deck__view-tabs" aria-label="Home Page views">
            {VIEWS.map((view) => (
                <button
                    key={view.id}
                    ref={(node) => { tabRefs.current[view.id] = node; }}
                    className={`floor-deck__view-tab${displayedActiveView === view.id ? ' is-active' : ''}`}
                    type="button"
                    aria-pressed={displayedActiveView === view.id}
                    onClick={() => handleViewChange(view.id)}
                >
                    <view.Icon aria-hidden="true" />
                    {view.label}
                </button>
            ))}
            <span
                className={`floor-deck__view-tab-indicator${isIndicatorReady ? ' is-ready' : ''}`}
                style={{ transform: `translateX(${indicator.left}px)`, width: `${indicator.width}px` }}
                aria-hidden="true"
            />
        </nav>
    );
}

export { HomePageTabs };
